import React, { lazy, Suspense } from 'react';

import { Map } from 'immutable';
import { PasswordStrengthBarProps } from 'react-password-strength-bar';
import { EmployerProfile } from 'registration/types';

import { styled } from 'shared/components/ihcl/styled';
import { Input } from 'shared/components/ihcl/v2/input';
import { Spinner } from 'shared/components/ihcl/v2/spinner';

const PasswordStrength = lazy(
  () =>
    // TODO: Remove ts-ignore if change to tsconfig module to es2020 or later
    // @ts-ignore
    import(
      /* webpackChunkName: "password_strength" */
      'shared/components/PasswordStrength'
    )
);

const PasswordStrengthWrapper = styled('div', {
  marginTop: '16px',
  marginLeft: '145px',
});

type EmployerProfilePreRegistationFormProps = {
  companyName: string;
  email: string;
  employerProfile: EmployerProfile;
  errors: Map<string, string | boolean>;
  fullName: string;
  jobTitle: string;
  onPasswordScoreChange: PasswordStrengthBarProps['onChangeScore'];
  onTextFieldChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  password: string;
  phone: string;
};

const EmployerProfilePreRegistationForm = function ({
  companyName,
  email,
  employerProfile,
  errors,
  fullName,
  jobTitle,
  onPasswordScoreChange,
  onTextFieldChange,
  password,
  phone,
}: EmployerProfilePreRegistationFormProps) {
  return (
    <>
      <div className="mb-4">
        <input name="ep" type="hidden" value={employerProfile.encoded_email} />
        {employerProfile.sso_required && (
          <input
            name="user[employer_profile_attributes][sso_required]"
            type="hidden"
            value="true"
          />
        )}
        <div className="top-separator center-row">
          <label
            htmlFor="company-name"
            className="mb-1 block text-label-sm font-bold"
          >
            <span>Company name</span>
          </label>
          <Input
            id="company-name"
            name="user[employer_profile_attributes][company_name]"
            type="text"
            value={companyName}
            status={errors.get('companyName') ? 'error' : 'default'}
            readOnly
          />
        </div>
        <div>
          <p className="mt-1 text-paragraph-xs text-negative">
            {errors.get('companyName', '')}
          </p>
        </div>
      </div>

      <div className="mb-4">
        <div className="top-buffer center-row">
          <label htmlFor="email" className="mb-1 block text-label-sm font-bold">
            <span>Email</span>
          </label>
          <Input
            id="email"
            name="user[email]"
            type="text"
            value={email}
            status={errors.get('email') ? 'error' : 'default'}
            readOnly
          />
        </div>
        <div>
          <p className="mt-1 text-paragraph-xs text-negative">
            {errors.get('email', '')}
          </p>
        </div>
      </div>

      <div className="mb-4">
        <div className="top-buffer center-row">
          <label
            htmlFor="full-name"
            className="mb-1 block text-label-sm font-bold"
          >
            <span>Full name</span>
          </label>
          <Input
            id="full-name"
            autoFocus
            name="user[employer_profile_attributes][full_name]"
            onChange={onTextFieldChange}
            type="text"
            value={fullName}
            status={errors.get('fullName') ? 'error' : 'default'}
          />
        </div>
        <div>
          <p className="mt-1 text-paragraph-xs text-negative">
            {errors.get('fullName', '')}
          </p>
        </div>
      </div>

      {employerProfile.sso_required ? (
        <div className="top-buffer center-row">
          <p className="mt-1 text-paragraph-xs text-gray-label">
            Sign-in for this email is handled by your organization&apos;s single
            sign-on. You won&apos;t need to create a password.
          </p>
        </div>
      ) : (
        <div className="mb-4">
          <div className="top-buffer center-row">
            <label
              htmlFor="password"
              className="mb-1 block text-label-sm font-bold"
            >
              <span>Create password</span>
            </label>
            <Input
              id="password"
              name="user[password]"
              onChange={onTextFieldChange}
              type="password"
              value={password}
            />
          </div>
          <PasswordStrengthWrapper>
            <Suspense fallback={<Spinner className="size-6" />}>
              <PasswordStrength
                password={password}
                onPasswordScoreChange={onPasswordScoreChange}
              />
            </Suspense>
          </PasswordStrengthWrapper>
        </div>
      )}

      <div className="mb-4">
        <div className="top-buffer center-row">
          <label htmlFor="phone" className="mb-1 block text-label-sm font-bold">
            <span>Phone number</span>
          </label>
          <Input
            id="phone"
            name="user[employer_profile_attributes][phone_and_extension]"
            onChange={onTextFieldChange}
            type="text"
            value={phone}
            status={errors.get('phone') ? 'error' : 'default'}
          />
        </div>
        <div>
          <p className="mt-1 text-paragraph-xs text-negative">
            <span className="sentry-mask">{errors.get('phone', '')}</span>
          </p>
        </div>
      </div>

      <div className="mb-4">
        <div className="top-buffer center-row">
          <label
            htmlFor="job-title"
            className="mb-1 block text-label-sm font-bold"
          >
            <span>Job title</span>
          </label>
          <Input
            id="job-title"
            name="user[employer_profile_attributes][job_title]"
            onChange={onTextFieldChange}
            type="text"
            value={jobTitle}
            status={errors.get('jobTitle') ? 'error' : 'default'}
          />
        </div>
        <div>
          <p className="mt-1 text-paragraph-xs text-negative">
            {errors.get('jobTitle', '')}
          </p>
        </div>
      </div>
    </>
  );
};

export default EmployerProfilePreRegistationForm;
